import React, { useEffect, useRef } from 'react';
import styles from './Hero.module.css';

interface Particle {
  x: number;
  y: number;
  r: number;
  dx: number;
  dy: number;
  opacity: number;
}

const scrollTo = (id: string): void =>
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

export default function Hero(): React.ReactElement {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d')!;
    let raf: number;

    const particles: Particle[] = [];

    const resize = () => {
      canvas.width  = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    for (let i = 0; i < 60; i++) {
      particles.push({
        x:       Math.random() * canvas.width,
        y:       Math.random() * canvas.height,
        r:       Math.random() * 1.5 + 0.3,
        dx:      (Math.random() - 0.5) * 0.3,
        dy:      (Math.random() - 0.5) * 0.3,
        opacity: Math.random() * 0.4 + 0.1,
      });
    }

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.forEach((p) => {
        p.x += p.dx;
        p.y += p.dy;
        if (p.x < 0 || p.x > canvas.width)  p.dx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.dy *= -1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(0,229,255,${p.opacity})`;
        ctx.fill();
      });

      particles.forEach((p1, i) => {
        particles.slice(i + 1).forEach((p2) => {
          const dist = Math.hypot(p1.x - p2.x, p1.y - p2.y);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(p1.x, p1.y);
            ctx.lineTo(p2.x, p2.y);
            ctx.strokeStyle = `rgba(0,229,255,${0.06 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        });
      });

      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <section id="hero" className={styles.hero}>
      <canvas ref={canvasRef} className={styles.canvas} aria-hidden="true" />
      <div className={styles.blob1} aria-hidden="true" />
      <div className={styles.blob2} aria-hidden="true" />

      <div className={styles.content}>
        <p className={styles.greeting}>&gt; Hello, I'm</p>

        <h1 className={styles.name}>
          Tharanga<br />
          <span className={styles.nameAccent}>Mahavila</span>
        </h1>

        <h2 className={styles.title}>Senior Fullstack Developer</h2>

        <p className={styles.description}>
          7+ years building scalable microservices, cloud-native systems, and event-driven architectures.
          Based in <strong>Sweden</strong> — open to remote &amp; EU opportunities.
        </p>

        <div className={styles.buttons}>
          <button className={styles.btnPrimary} onClick={() => scrollTo('contact')}>
            Get In Touch →
          </button>
          <button className={styles.btnSecondary} onClick={() => scrollTo('projects')}>
            View Work
          </button>
        </div>

        <div className={styles.socials}>
          {[
            { label: 'LinkedIn', url: 'https://www.linkedin.com/in/tharanga-mahavila-7b9994199' },
            { label: 'GitHub',   url: 'https://github.com/TharangaMahavila' },
            { label: 'Email',    url: 'mailto:tharangamahavila@gmail.com' },
          ].map(({ label, url }) => (
            <a key={label} href={url} target="_blank" rel="noopener noreferrer" className={styles.socialLink}>
              {label}
            </a>
          ))}
        </div>
      </div>

      {/* Profile photo */}
      <div className={styles.photoWrapper}>
        <div className={styles.photoFrame}>
          <div className={styles.photoCorner} aria-hidden="true" />
          <img
            src="/profile.jpeg"
            alt="Tharanga Mahavila — Senior Fullstack Developer"
            className={styles.photo}
          />
          <div className={styles.photoBadge}>
            <span className={styles.photoBadgeText}>🟢 Open to work</span>
            <span className={styles.photoBadgeSub}>Senior / Lead roles · Europe</span>
          </div>
        </div>
      </div>

      <div className={styles.scrollIndicator} aria-hidden="true">
        <div className={styles.scrollLine} />
      </div>
    </section>
  );
}
